/* eslint-disable max-len */
/* global COMSCORE */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import Schedule from 'components/schedule';
import searchAPI from 'utils/searchAPI';

import VideoThumbnail from '../video-thumbnail';
import ShowMore from './components/show-more';
import ExpandableSearch from './components/expandable-search';
import './style.scss';

const {
  i18n: {
    __,
  },
} = wp;

export default class VideoHub extends PureComponent {
  /**
   * Set PropTypes for this component.
   *
   * @type {object}
   */
  static propTypes = {
    categoryMenuItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    initialCategoryVideos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    initialCategoryPages: PropTypes.number,
    initialCategoryBase: PropTypes.string.isRequired,
    setActiveVideo: PropTypes.oneOfType([
      PropTypes.func, PropTypes.bool,
    ]).isRequired,
    topLevelMenuItems: PropTypes.arrayOf(PropTypes.string).isRequired,
    showSearch: PropTypes.bool.isRequired,
  };

  /**
   * Set defaultProps for this component.
   *
   * @type {object}
   */
  static defaultProps = {
    initialCategoryPages: 1,
  };

  /**
   * Format categories for React Select.
   *
   * @param {Array} categories
   * @returns {Array}
   */
  static formatSelectCategories(categories) {
    if (! categories) {
      return [];
    }

    return categories.map((category) => ({
      label: parse(DOMPurify.sanitize(category.name)),
      noid: category.noid,
      value: category.slug,
    }));
  }

  constructor(props) {
    super(props);

    const live = window.location.pathname.includes('live') || window.location.pathname.includes('en-vivo') ? 1 : 0;

    this.state = {
      activeCategory: 0 < props.categoryMenuItems.length ? {
        label: props.categoryMenuItems[0].name, // eslint-disable-line react/prop-types
        value: props.categoryMenuItems[0].slug, // eslint-disable-line react/prop-types
      } : {
        label: __('Local', 'nbc'),
        value: 'local',
      },
      categoryPage: 1,
      searchPage: 1,
      categoryVideos: props.initialCategoryVideos || [],
      totalCategoryPages: props.initialCategoryPages || 1,
      loading: false,
      showMoreLoading: false,
      searchShowMoreLoading: false,
      hideShowMoreSearch: true,
      topMenu: live,
      searchResults: [],
      hasSearchResults: false,
      hasSearchQuery: false,
      hasSearched: false,
      searchQuery: '',
      toggleSearch: ! (window.location.pathname.includes('live') || window.location.pathname.includes('en-vivo')),
    };

    this.perPage = 12;
    this.breakpoints = {
      tablet: 820,
    };
  }

  /**
   * Set search results.
   * @param {Array} searchResults
   */
  setSearchResults = (searchResults) => {
    const { items } = searchResults;

    if (items && 1 <= items.length) {
      const { total_pages } = searchResults.pagination; // eslint-disable-line camelcase

      this.setState((prevState) => ({
        ...prevState,
        searchResults: [...items],
        hasSearchResults: true,
        hasSearched: true,
        hideShowMoreSearch: 1 >= total_pages, // eslint-disable-line camelcase
        searchPage: 1,
      }), () => this.setState({ loading: false }));
    } else {
      this.setState({
        searchResults: [],
        hasSearchResults: false,
        hasSearched: true,
        hideShowMoreSearch: true,
        searchPage: 1,
      }, () => this.setState({ loading: false }));
    }
  };

  /**
   * Set show more search results.
   *
   * @param searchResults
   */
  setShowMoreSearchResults = (searchResults) => {
    this.setState((prevState) => {
      const previousResults = prevState.searchResults;
      const { total_pages } = searchResults.pagination; // eslint-disable-line camelcase

      return {
        ...prevState,
        searchResults: [
          ...previousResults,
          ...searchResults.items,
        ],
        searchPage: prevState.searchPage + 1,
        hideShowMoreSearch: (prevState.searchPage + 2) > total_pages, // eslint-disable-line camelcase
        searchShowMoreLoading: false,
      };
    }, () => this.setState({ loading: false }));
  };

  /**
   * Set the active top menu item.
   *
   * @param {Number} index
   */
  setTopMenuIndex = (index, search) => {
    this.setState({ topMenu: index });
    const fromSearch = ('undefined' !== search) ? search : false;
    if (0 === index && ! fromSearch) {
      // Set initial videos, just like visiting /videos.
      this.setState({ categoryVideos: this.props.initialCategoryVideos });
    }
  };

  /**
   * Keep track of the search query.
   *
   * @param searchQuery
   */
  setSearchQuery = (searchQuery) => {
    this.setState({ searchQuery });
  };

  /**
   * Set the active category menu.
   *
   * @param {Object} activeCategory
   */
  setActiveCategory = (activeCategory) => {
    this.setState({ activeCategory });
  };

  /**
   * Set isLoading state.
   *
   * @param {Boolean} loading
   */
  setIsLoading = (loading) => {
    this.setState({ loading });
  };

  /**
   * Set has query.
   *
   * @param {Boolean} hasSearchQuery
   */
  setHasSearchQuery = (hasSearchQuery) => {
    this.setState({ hasSearchQuery });
  };

  /**
   * Set has searched.
   *
   * @param {Boolean} hasSearched
   */
  setHasSearched = (hasSearched) => {
    this.setState({ hasSearched });
  };

  /**
   * Get search results when clicking show more.
   */
  getShowMoreSearchResults = () => {
    const { searchQuery, searchPage } = this.state;

    if (1 <= searchQuery.length) {
      this.setState({ searchShowMoreLoading: true });

      searchAPI.searchVideos(searchQuery, searchPage + 1)
        .then((res) => {
          this.setShowMoreSearchResults(res.template_items);
        });
    } else {
      this.setShowMoreSearchResults([]);
      this.setHasSearched(false);
    }
  };

  /**
   * Fetch categories.
   *
   * @param {String} category Category slug.
   * @param {Number} page The page of videos.
   * @returns {Promise}
   */
  fetchCategories = (noid, page = 1) => {
    const {
      url: {
        addQueryArgs,
      },
    } = wp;
    const {
      initialCategoryBase,
    } = this.props;

    const path = addQueryArgs(initialCategoryBase, {
      _fields: 'template_items',
      category_noid: noid,
      page,
      per_page: this.perPage,
    });
    return fetch(path);
  };

  /**
   * Fetch videos by category.
   *
   * @param {String} category Category NOID.
   * @param {Number} page The page of videos.
   */
  fetchVideosByCategoryNoid = (noid, page = 1) => {
    const categories = this.fetchCategories(noid, page);
    this.setState({ loading: true });

    categories
      .then((response) => response.json())
      .then((response) => {
        const videos = response.template_items.items || [];
        this.setState({
          categoryVideos: videos,
          categoryPage: 1,
          totalCategoryPages: response.template_items.pagination.total_pages || 1,
        });
        this.setState({ loading: false });
      });
  };

  handleToggleState = () => {
    this.setState((state) => ({ toggleSearch: ! state.toggleSearch }));
  };

  /**
   * Handle clicking the show more button.
   */
  handleShowMore = () => {
    const {
      categoryPage,
      activeCategory: {
        noid: category,
      },
    } = this.state;

    const newPage = categoryPage + 1;
    const categories = this.fetchCategories(category, newPage);

    this.setState({ showMoreLoading: true });
    categories
      .then((response) => response.json())
      .then((response) => {
        const videos = response.template_items.items || [];
        this.setState((prevState) => ({
          categoryVideos: [
            ...prevState.categoryVideos,
            ...videos,
          ],
          categoryPage: newPage,
          showMoreLoading: false,
        }));
      });
    // eslint-disable-next-line no-unused-expressions
    window.COMSCORE && COMSCORE.beacon({ c1: '2', c2: '6035083' });
  };

  /**
   * Handle clicking show more.
   */
  handleSearchShowMore = () => {
    this.getShowMoreSearchResults();
  };

  render() {
    const {
      categoryMenuItems,
      setActiveVideo,
      topLevelMenuItems,
      showSearch,
      tvSchedule,
    } = this.props;
    const {
      activeCategory,
      categoryVideos,
      loading,
      showMoreLoading,
      searchShowMoreLoading,
      hideShowMoreSearch,
      categoryPage,
      totalCategoryPages,
      topMenu,
      hasSearchResults,
      searchResults,
      hasSearchQuery,
      hasSearched,
      toggleSearch,
    } = this.state;

    const selectCategories = VideoHub.formatSelectCategories(categoryMenuItems);

    const loadingClass = classNames({
      'tabbed-content__video-grid-wrapper': true,
      'tabbed-content__video-grid-wrapper--loading': loading,
    });

    return (
      <div className="tabbed-content">
        <div className="tabbed-content__row tabbed-content__row--top">
          <ul className="tabbed-content__menu tabbed-content__menu-top">
            {
              topLevelMenuItems &&
                topLevelMenuItems.map((item, i) => {
                  const classes = classNames({
                    'tabbed-content__button-link': true,
                    'tabbed-content__button-link--active-border': i === topMenu,
                    'tabbed-content__button-link--inactive': 1 === topLevelMenuItems.length,
                  });

                  return (
                    <li key={item} className="tabbed-content__menu-item">
                      <button
                        className={classes}
                        onClick={() => {
                          if (1 === topLevelMenuItems.length) {
                            return;
                          }
                          this.setTopMenuIndex(i);
                          this.handleToggleState();
                        }}
                        type="button"
                      >
                        {parse(DOMPurify.sanitize(item))}
                      </button>
                    </li>
                  );
                })
            }
          </ul>
          {showSearch && toggleSearch && ! tvSchedule && (
            <ExpandableSearch
              setSearchResults={this.setSearchResults}
              setIsLoading={this.setIsLoading}
              setHasSearchQuery={this.setHasSearchQuery}
              setHasSearched={this.setHasSearched}
              setSearchQuery={this.setSearchQuery}
              setTopMenuIndex={this.setTopMenuIndex}
              handleToggleState={this.state.toggleSearch}
            />
          )}
        </div>
        <div className="tabbed-content__divider" />
        <div className="tabbed-content__row">
          {
            (0 === topMenu && hasSearchQuery && ! tvSchedule) &&
            (
              <div className="tabbed-content__panel">
                <div className="tabbed-content__video-grid">
                  <div className={loadingClass}>
                    {
                      (! loading && hasSearched) && (
                        <h3
                          className="tabbed-content__section-title"
                        >
                          {__('Search Results', 'nbc')}
                        </h3>
                      )
                    }
                    <div className="tabbed-content__videos">
                      {
                        (! loading && hasSearchResults) &&
                          searchResults.map((video) => (
                            <VideoThumbnail
                              key={video.network_object_id}
                              setActiveVideo={setActiveVideo}
                              video={video}
                              horizontal
                            />
                          ))
                      }
                      {
                        (! loading && ! hasSearchResults && hasSearched) &&
                        (
                          <h3
                            className="tabbed-content__section-title no-results"
                          >
                            {__('No Results', 'nbc')}
                          </h3>
                        )
                      }
                    </div>
                  </div>
                  <div className="tabbed-content__show-more">
                    {
                      (false === hideShowMoreSearch && ! loading) &&
                      (
                        <ShowMore
                          showMore={this.handleSearchShowMore}
                          cssClass={`show-more${searchShowMoreLoading ? ' loading' : ''}`}
                          perPage={this.perPage}
                          page={categoryPage}
                          category={activeCategory.value}
                          disabled={searchShowMoreLoading}
                        />
                      )
                    }
                  </div>
                </div>
              </div>
            )
          }
          {
            (0 === topMenu && ! hasSearchQuery && ! tvSchedule) &&
            (
              <div className="tabbed-content__panel">
                <div className="tabbed-content__video-grid">
                  {
                    <MediaQuery minWidth={this.breakpoints.tablet + 1}>
                      <ul className="tabbed-content__menu">
                        {
                          selectCategories &&
                          selectCategories.map((selectedCategory) => {
                            const { label, noid } = selectedCategory;
                            const classes = classNames({
                              'tabbed-content__button-link': true,
                              'tabbed-content__button-link--active':
                                noid === activeCategory.noid,
                            });
                            return (
                              <li
                                key={selectedCategory.value}
                                className="tabbed-content__menu-item"
                              >
                                <button
                                  className={classes}
                                  onClick={() => {
                                    this.fetchVideosByCategoryNoid(noid, 1);
                                    this.setActiveCategory(selectedCategory);
                                  }}
                                  type="button"
                                >
                                  {parse(DOMPurify.sanitize(label))}
                                </button>
                              </li>
                            );
                          })
                        }
                      </ul>
                    </MediaQuery>
                  }
                  {
                    <MediaQuery maxWidth={this.breakpoints.tablet}>
                      <select
                        value={activeCategory.value}
                        defaultValue={activeCategory.label}
                        onChange={(e) => {
                          const selectedValue = e.currentTarget.value;
                          const selectedCategory = selectCategories.find(({ value }) => value === selectedValue);
                          this.fetchVideosByCategoryNoid(selectedCategory.noid);
                          this.setActiveCategory(selectedCategory);
                        }}
                        className="tabbed-content__select"
                      >
                        {
                          selectCategories.map(({ label, value }) => <option value={value}>{label}</option>)
                        }
                      </select>
                    </MediaQuery>
                  }
                  <div className={loadingClass}>
                    {
                      ! loading && (
                        <h3
                          className="tabbed-content__section-title"
                        >
                          {__('Latest', 'nbc')}
                        </h3>
                      )
                    }
                    <div className="tabbed-content__videos">
                      {
                        (! loading && 0 < categoryVideos.length) &&
                        categoryVideos.map((video) => (
                          <VideoThumbnail
                            key={video.network_object_id}
                            setActiveVideo={setActiveVideo}
                            video={video}
                            horizontal
                          />
                        ))
                      }
                    </div>
                  </div>
                  <div className="tabbed-content__show-more">
                    {
                      (
                        0 < categoryVideos.length &&
                        categoryPage < totalCategoryPages &&
                        ! loading
                      ) && (
                        <ShowMore
                          showMore={this.handleShowMore}
                          cssClass={`show-more${showMoreLoading ? ' loading' : ''}`}
                          perPage={this.perPage}
                          page={categoryPage}
                          category={activeCategory.value}
                          disabled={showMoreLoading}
                        />
                      )
                    }
                  </div>
                </div>
              </div>
            )
          }
          {
            (1 === topMenu || tvSchedule) && <Schedule
              tvSchedule={tvSchedule}
            />
          }
        </div>
      </div>
    );
  }
}
/* eslint-enable max-len */
